import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  take,
  tap,
} from 'rxjs/operators';

import { WebflowInstanceService } from '@libs/src/webflow/webflow-instance.service';
import { LocalStorageCheckerService } from '@main-client/src/app/core/local-storage-checker.service';
import { LocaleSettingsService } from '@main-client/src/app/core/locale-settings.service';
import { WindowRefService } from '@main-client/src/app/window-ref.service';

import { fadeInGrowEnter } from '@libs/src/animations/animations';
import { locales } from '@libs/src/locale/locale.helper';
import { Locale } from '@libs/src/locale/locale.interface';
import { Account } from '@libs/src/models/account.model';
import { PathwayUnpopulated } from '@libs/src/models/pathway.interface';
import { Program } from '@libs/src/models/program.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { IAppState } from '@main-client/src/app/app.state';

import isEmpty from 'lodash-es/isEmpty';
import unset from 'lodash-es/unset';

@Component({
  animations: [fadeInGrowEnter],
  selector: 'app-sign-up-view',
  templateUrl: './sign-up-view.component.html',
})
export class SignUpViewComponent implements OnInit {
  canSignUp = true;
  localizationDisabledForInvitingGroup = false;
  hasWebflowInstance = false;
  programSignupText = '';
  pathwaySignupText = '';
  selectedLocale: Locale;
  supportedLocales: Locale[] = locales;
  tenant$ = this.store.select('tenant');
  loginQueryParams = {
    redirectUrl: this.route.snapshot.queryParams.redirectUrl,
  };
  tenant: Tenant;
  programName: Program['name'];
  pathwayName: PathwayUnpopulated['name'];
  preregistrationAccount: Account;
  showSSOView = false;

  constructor(
    @Inject(LOCALE_ID) private readonly appLocale: string,
    private readonly localeSettingsService: LocaleSettingsService,
    private readonly localStorageChecker: LocalStorageCheckerService,
    private readonly store: Store<IAppState>,
    private readonly router: Router,
    private readonly windowService: WindowRefService,
    public route: ActivatedRoute,
    public webflowInstanceService: WebflowInstanceService,
  ) {}

  ngOnInit() {
    this.isLocalizationDisabledForInvitingGroup(this.route.snapshot.data);
    this.selectedLocale = locales.find(
      (locale) => locale.code === this.appLocale,
    );
    const isWebflowRedirect = ['email', 'firstName', 'lastName'].every(
      (fieldName) => !isEmpty(this.route.snapshot.queryParamMap.get(fieldName)),
    );
    this.canSignUp = this.localStorageChecker.hasLocalStorage();
    this.programSignupText = $localize`:@@signupView.signupBanner.programSignupText:Join to participate in the ${this.route.snapshot.data?.programMembership?.program?.name}`;
    this.pathwaySignupText = $localize`:@@signupView.signupBanner.pathwaySignupText:Join to participate in the ${this.route.snapshot.data?.pathwayMembership?.pathway?.name}`;
    this.tenant$
      .pipe(
        filter((tenant) => !isEmpty(tenant)),
        tap((tenant) => {
          this.tenant = tenant;
          this.showSSOView = tenant?.integrations?.workos?.sso_signup_enabled;
        }),
        filter(() => !isWebflowRedirect),
        take(1),
        concatMap((tenant: Tenant) =>
          this.webflowInstanceService
            .getWebflowPage({
              url: `/schools/${tenant.name}`,
            })
            .pipe(catchError(() => EMPTY)),
        ),
        delay(800),
        tap(() => {
          this.hasWebflowInstance = true;
        }),
      )
      .subscribe();
    this.preregistrationAccount =
      this.route.snapshot.data?.preRegistration?.account;
    this.programName =
      this.route.snapshot.data?.programMembership?.program?.name;
    this.pathwayName =
      this.route.snapshot.data?.pathwayMembership?.pathway?.name;
  }

  isLocalizationDisabledForInvitingGroup(data: {
    [key: string | symbol]: Data;
  }): void {
    const invitation = data.invitation;
    const preregistration = data.preRegistration;
    this.localizationDisabledForInvitingGroup =
      false === invitation?.group?.features_enabled?.localization ||
      false ===
        preregistration?.decodedToken?.group_features_enabled?.localization;
  }

  async resetPreRegistrationId() {
    const queryParams = { ...this.route.snapshot.queryParams };
    unset(queryParams, 'preRegistrationId');
    await this.router.navigate(['/signup']);
    this.windowService.nativeWindow.location.reload();
  }

  showSSOViewHandler(value: boolean) {
    this.showSSOView = value;
  }
}
